import { fractionLabel } from "@/lib/food-configuration";
import type {
  PublicOrderResponse,
} from "@/types/order";

export function formatCurrency(
  value: string | number,
) {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(Number(value));
}

export function normalizeWhatsapp(
  value: string,
) {
  return value.replace(/\D/g, "");
}

export function buildOrderWhatsappUrl(
  whatsapp: string,
  companyName: string,
  order: PublicOrderResponse,
  siteOrigin: string,
) {
  const number = normalizeWhatsapp(whatsapp);

  const normalizedOrigin =
    siteOrigin.replace(/\/$/, "");

  const trackingUrl =
    `${normalizedOrigin}${order.tracking_path}`;

  const itemLines = order.items.map(
    (item, index) => {
      const variationLines =
        item.variation_values.map(
          (variationValue) =>
            `${variationValue.attribute}: ${variationValue.value}`,
        );

      const sizeLines =
        item.size
          ? [`Tamanho: ${item.size}`]
          : [];

      const optionLines = (
        item.option_selections ?? []
      ).map(
        (option) =>
          `${option.group_name}: ${fractionLabel(option.fraction)} ${option.option_name}${
            Number(option.price_delta) > 0
              ? ` (+ ${formatCurrency(
                  option.price_delta,
                )})`
              : ""
          }`,
      );

      const observationLines =
        item.observation
          ? [`Obs.: ${item.observation}`]
          : [];

      return [
        `${index + 1}. ${item.product_name}`,
        ...sizeLines,
        ...variationLines,
        ...optionLines,
        ...observationLines,
        `Quantidade: ${item.quantity}`,
        `Valor unitário: ${formatCurrency(
          item.unit_price,
        )}`,
        `Subtotal: ${formatCurrency(
          item.subtotal,
        )}`,
      ].join("\n");
    },
  );

  const addressParts = [
    order.customer_address,
    order.customer_number,
  ].filter(Boolean);

  const cityLine = [
    order.customer_district,
    order.customer_city,
    order.customer_state,
  ]
    .filter(Boolean)
    .join(" - ");

  const messageParts = [
    `Olá! Fiz o pedido ${order.number} pela plataforma Fornecedor.net para ${companyName}.`,
    "",
    `Cliente: ${order.customer_name}`,
    `Telefone: ${order.customer_phone}`,
  ];

  /*
   * Endereço só faz sentido quando haverá entrega.
   * Retirada no estabelecimento não exige endereço.
   */
  if (order.delivery_type !== "pickup") {
    messageParts.push(
      "",
      "Endereço para entrega:",
    );

    if (addressParts.length) {
      messageParts.push(
        addressParts.join(", "),
      );
    }

    if (order.customer_complement.trim()) {
      messageParts.push(
        `Complemento: ${order.customer_complement}`,
      );
    }

    if (cityLine) {
      messageParts.push(cityLine);
    }

    if (order.customer_cep) {
      messageParts.push(
        `CEP: ${order.customer_cep}`,
      );
    }
  }

  if (order.customer_notes.trim()) {
    messageParts.push(
      "",
      `Observações: ${order.customer_notes}`,
    );
  }

  messageParts.push(
    "",
    ...itemLines.flatMap((line) => [
      line,
      "",
    ]),
    `Subtotal: ${formatCurrency(
      order.subtotal,
    )}`,
  );

  if (
    order.coupon_code &&
    Number(order.discount_amount) > 0
  ) {
    messageParts.push(
      `Cupom: ${order.coupon_code}`,
      `Desconto: -${formatCurrency(
        order.discount_amount,
      )}`,
    );
  }

  /*
   * Forma de recebimento.
   */
  if (order.delivery_type === "pickup") {
    messageParts.push(
      "Forma de recebimento: Retirada no local",
      "Taxa de entrega: Grátis",
    );
  } else if (
    order.delivery_type === "local_delivery"
  ) {
    messageParts.push(
      "Forma de recebimento: Entrega local",
    );

    if (order.delivery_region_name) {
      messageParts.push(
        `Região: ${order.delivery_region_name}`,
      );
    }

    messageParts.push(
      Number(order.shipping_price) > 0
        ? `Taxa de entrega: ${formatCurrency(
            order.shipping_price,
          )}`
        : "Taxa de entrega: Grátis",
    );
  } else if (order.shipping_service) {
    const shippingLabel = [
      order.shipping_service,
      order.shipping_company,
    ]
      .filter(Boolean)
      .join(" - ");

    messageParts.push(
      `Entrega: ${shippingLabel}`,
      Number(order.shipping_price) > 0
        ? `Valor do frete: ${formatCurrency(
            order.shipping_price,
          )}`
        : "Valor do frete: Grátis",
    );

    if (order.shipping_delivery_time) {
      messageParts.push(
        `Prazo estimado: ${order.shipping_delivery_time} dia${
          order.shipping_delivery_time === 1
            ? ""
            : "s"
        } úteis`,
      );
    }
  }

  messageParts.push(
    `Total estimado: ${formatCurrency(
      order.total,
    )}`,
  );

  if (order.payment_method === "pix") {
    messageParts.push(
      "Forma de pagamento: PIX",
      "Envie o comprovante do PIX nesta conversa.",
    );
  } else if (
    order.payment_method === "card_on_delivery"
  ) {
    messageParts.push(
      "Forma de pagamento: Cartão na entrega",
    );
  } else if (
    order.payment_method === "cash_on_delivery"
  ) {
    messageParts.push(
      "Forma de pagamento: Dinheiro na entrega",
    );

    if (order.cash_change_for) {
      messageParts.push(
        `Troco para: ${formatCurrency(
          order.cash_change_for,
        )}`,
      );
    }
  }

  messageParts.push(
    "",
    "Pedido registrado pela plataforma Fornecedor.net.",
    "",
    "Acompanhe seu pedido:",
    trackingUrl,
    "",
    order.delivery_type === "pickup"
      ? "Gostaria de confirmar a disponibilidade e a retirada."
      : "Gostaria de confirmar a disponibilidade e a entrega.",
  );

  return (
    `https://wa.me/${number}?text=` +
    encodeURIComponent(
      messageParts.join("\n"),
    )
  );
}
